import React, {useCallback, useState} from 'react';

import debounce from 'lodash/debounce';

import {MaxMembersLimitRow} from 'app/account/components/MaxMembersLimitRow';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {useCurrentUserState} from 'app/current-user/api';
import {CheckboxField} from 'app/general/widgets/CheckboxField';
import {EditableLogo} from 'app/general/widgets/EditableLogo';
import {TextboxField} from 'app/general/widgets/TextboxField';
import {SafeAIStatement} from 'app/openai/components/SafeAIStatement';
import {
  useAccountSettingsState,
  useUpdateAccountSettings,
} from 'app/settings/api';
import {ACCOUNT_SWITCH_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import type {AccountSettingsSerializedType} from 'sweet-types';

export function CompanyInformation() {
  const account = useAccountSettingsState().data!;
  const {is_owner} = useCurrentUserState().data!;

  return (
    <div className="mb-4">
      <h4 className="section">{gt('Company Information')}</h4>
      <CompanyInfoForm account={account} canEdit={is_owner} />
    </div>
  );
}

function BrandTagLabel({is_paying}: {is_paying: boolean}) {
  return (
    <>
      {gt('Enable SweetProcess Branding')}
      {!is_paying && (
        <LazyTooltip
          tooltip={gt(
            'This feature is available only to paid accounts. Please upgrade your plan to unlock this setting.'
          )}
        >
          <i className="text-muted ml-1 far fa-info-circle" />
        </LazyTooltip>
      )}
    </>
  );
}

interface FormProps {
  account: AccountSettingsSerializedType;
  canEdit: boolean;
}

const CompanyInfoForm: React.FC<FormProps> = ({account, canEdit}) => {
  const [formData, setFormData] = useState({
    company_name: account.company_name,
    industry_classification: account.industry_classification,
    require_ai_data_sharing_at: account.require_ai_data_sharing_at,
    is_2fa_required: account.is_2fa_required,
    is_brand_tag_enabled: account.is_brand_tag_enabled,
    can_export: account.can_export,
    can_edit_tags: account.can_edit_tags,
    can_see_all_tasks: account.can_see_all_tasks,
    can_assign_tasks: account.can_assign_tasks,
  });
  const [updateAccountSettings] = useUpdateAccountSettings();

  const debouncedSave = useCallback(
    debounce((newFormData: Parameters<typeof updateAccountSettings>[0]) => {
      updateAccountSettings(newFormData);
    }, 500),
    []
  );

  const handleDelayChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const {name, value} = e.target;
    debouncedSave({[name]: value, account_id: account.account_id});
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, checked} = e.target;
    updateAccountSettings({[name]: checked, account_id: account.account_id});
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  if (!canEdit) {
    return <SwitchAccountInformation name={account.company_name} />;
  }

  return (
    <div className="pb-4 form">
      <TextboxField
        label={gt('Account name')}
        id="id_company_name"
        name="company_name"
        onChange={handleDelayChange}
        value={formData.company_name}
      />

      <TextboxField
        label={gt('Industry Classification')}
        helpText={gt('Used by the SweetAI to tailor suggested documents.')}
        id="id_industry_classification"
        name="industry_classification"
        onChange={handleDelayChange}
        value={formData.industry_classification}
      />

      <MaxMembersLimitRow />
      <CheckboxField
        label={
          <>
            <i className="fa fa-robot mr-2" />
            {gt('Allow AI data share')}
            <SafeAIStatement />
          </>
        }
        name="require_ai_data_sharing_at"
        id="id_require_ai_data_sharing_at"
        helpText={gt(
          'Allow your data to be passed to the AI solution used by SweetProcess.'
        )}
        onChange={handleCheckboxChange}
        value={formData.require_ai_data_sharing_at}
      />

      <CheckboxField
        label={gt('Require Two Factor Authentication (2FA)')}
        name="is_2fa_required"
        id="id_is_2fa_required"
        helpText={gt(
          'Force all account members to use two factor authentication. Members will be asked to add two factor authentication on their next log in.'
        )}
        onChange={handleCheckboxChange}
        value={formData.is_2fa_required}
      />

      <CheckboxField
        label={<BrandTagLabel is_paying={account.is_paying} />}
        name="is_brand_tag_enabled"
        id="id_is_brand_tag_enabled"
        disabled={!account.is_paying}
        helpText={gt(
          'Show the SweetProcess branding "Powered by SweetProcess" and "Created with SweetProcess" to acknowledge the tools used to create your content.'
        )}
        onChange={handleCheckboxChange}
        value={formData.is_brand_tag_enabled}
      />

      <CheckboxField
        label={gt('Export PDF and Word')}
        name="can_export"
        id="id_can_export"
        helpText={gt('Regular members can export PDF and Word documents.')}
        onChange={handleCheckboxChange}
        value={formData.can_export}
      />
      <CheckboxField
        label={gt('Add Existing Tags')}
        name="can_edit_tags"
        id="id_can_edit_tags"
        helpText={gt(
          'Regular members can add existing tags to procedures, processes and policies.'
        )}
        onChange={handleCheckboxChange}
        value={formData.can_edit_tags}
      />
      <CheckboxField
        label={gt('See All Tasks')}
        name="can_see_all_tasks"
        id="id_can_see_all_tasks"
        helpText={gt(
          'Regular members can see all tasks for documents they have permission to see. Even if they were not assigned to the task.'
        )}
        onChange={handleCheckboxChange}
        value={formData.can_see_all_tasks}
      />
      <CheckboxField
        label={gt('Assign Tasks')}
        name="can_assign_tasks"
        id="id_can_assign_tasks"
        helpText={gt('Regular members assign tasks to other people.')}
        onChange={handleCheckboxChange}
        value={formData.can_assign_tasks}
      />
      <div className="form-group row">
        <label className="col-form-label col-xl-2 col-lg-3 col-md-3 col-sm-4">
          {gt('Current company logo')}
        </label>

        <EditableLogo
          onUpdate={(company_logo: string) =>
            debouncedSave({
              company_logo,
              account_id: account.account_id,
            })
          }
        />
      </div>
    </div>
  );
};

function SwitchAccountInformation({name}: {name: string}) {
  const {num_accounts} = useCurrentUserState().data!;
  return (
    <span className="d-flex align-items-center">
      <b className="mr-2">{gt('Account Name')}:</b> {name}
      {num_accounts > 1 ? (
        <FullHTMLLink
          html_url={ACCOUNT_SWITCH_URL}
          className="btn btn-link ml-4"
        >
          {gt('Switch Account')}
        </FullHTMLLink>
      ) : null}
    </span>
  );
}
